import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Tooltip } from 'antd';
import { Button, OnlyInDesktop, OnlyInTabletMobile, TourbookOverlay } from '@components/shared';
import { connect, ConnectedProps } from 'react-redux';
import { some } from 'lodash';
import { StoreState, Listing } from '@root/types';
import { getVisibleTourbookSummaries, tourbookCreateOnly } from '@store/selectors';
import useToggle from '@shared/useToggle';
import actions from '@store/actions/tourbooks';
import styles from './ActionButtons.module.less';

type Props = { listing: Listing; hasCurrentUser: boolean };

const mapState = (state: StoreState) => ({
  tourbooks: getVisibleTourbookSummaries(state),
  istourbookCreateOnly: tourbookCreateOnly(state),
});
const mapDispatch = {
  fetchTourbooks: () => actions.fetchTourbookSummaries(),
};

const connector = connect(mapState, mapDispatch);
type ReduxProps = ConnectedProps<typeof connector>;

export function RawTourbooks({
  listing,
  fetchTourbooks,
  tourbooks,
  hasCurrentUser,
  istourbookCreateOnly,
}: Props & ReduxProps) {
  const { t } = useTranslation('listing');
  const {
    value: tourbookOverlayIsOpen,
    setTrue: openTourbookOverlay,
    setFalse: closeTourbookOverlay,
  } = useToggle();

  useEffect(() => {
    if (hasCurrentUser) fetchTourbooks();
  }, [hasCurrentUser]);

  const inAnyTourbooks = some(tourbooks, tourbook => tourbook.listingIds.includes(listing.id));

  return (
    <span className={styles.tourbookContainer}>
      <OnlyInDesktop>
        <Tooltip
          trigger={['hover', 'click']}
          placement="bottom"
          title={t('tourbook.tourbookTooltip')}
        >
          <span>
            <Button
              className={classnames(styles.tourbookButton, inAnyTourbooks && styles.filled)}
              focus={tourbookOverlayIsOpen}
              type="tertiary"
              size="small"
              onClick={() => {
                openTourbookOverlay();
              }}
              icon={inAnyTourbooks ? 'tourbook-filled' : 'tourbook'}
            >
              <span className={styles.buttonText}>{t('tourbook.buttonText')}</span>
            </Button>
          </span>
        </Tooltip>
      </OnlyInDesktop>
      <OnlyInTabletMobile>
        <Button
          className={classnames(styles.tourbookButton, inAnyTourbooks && styles.filled)}
          type="tertiary"
          size="small"
          onClick={() => {
            openTourbookOverlay();
          }}
          icon={inAnyTourbooks ? 'tourbook-filled' : 'tourbook'}
        >
          <span className={styles.buttonText}>{t('tourbook.buttonText')}</span>
        </Button>
      </OnlyInTabletMobile>
      <TourbookOverlay
        isOpen={tourbookOverlayIsOpen}
        close={closeTourbookOverlay}
        listing={listing}
        createOnly={istourbookCreateOnly}
        tourbooks={tourbooks}
        className={styles.listingTourbookMobile}
      />
    </span>
  );
}

export default connector(RawTourbooks);
